import {ChangeDetectionStrategy, Component, HostListener} from '@angular/core'
import {map} from 'rxjs/operators'
import {Observable, zip} from 'rxjs'
import {ActivatedRoute} from '@angular/router'
import {ProWithProfile} from './public-pro-profile-resolver.service'
import {PageComponent, PageMetadata} from '../../../core/seo/page-metadata'
import {OptableLayout} from '../../../core/components/layout/optable-layout.interface'
import {TranslocoService} from '@ngneat/transloco'
import {OfferingsSection} from '../../../core/content/types/offering-section.types'
import {LinkService, OTHER_OFFERING_ID} from '../../../core/i18n/link.service'
import {faPlus} from '@fortawesome/pro-solid-svg-icons/faPlus'
import {PUBLIC_ROUTES} from '../../../shared/routing/public.routes'
import {BreakpointService} from '../../../craft/utils/breakpoint.service'
import {GalleryImage} from '../../../shared/components/gallery/gallery.component'
import {Locations} from '../../../craft/locations/locations.types'
import {ProAssociation, ProSpokenLanguage as ProSpokenLanguageGql} from '../../../../__generated__/types'
import {ProSpokenLanguage} from './language-flag/language-flag.component'
import {ConfigService} from '../../../core/config/config.service'
import {ProfileIntro} from '../../../craft/profile-introduction/profile-introduction.component'
import {Breadcrumb} from '../../../craft/breadcrumbs/breadcrumbs.component'
import {SupportService} from '../../../core/support/support.service'
import {pathToRequestOffering} from '../../../core/content/serde/offerings-section.serde'

@Component({
  selector: 'bgo-public-pro-profile',
  templateUrl: './public-pro-profile-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicProProfilePageComponent extends PageComponent implements OptableLayout {
  readonly pro$: Observable<ProWithProfile>
  readonly employeeCard$: Observable<{titleKey: string; numEmployees: number; numApprentices: number} | undefined>
  readonly offeringSection$: Observable<OfferingsSection | undefined>
  readonly gallery$: Observable<GalleryImage[]>
  readonly locations$: Observable<Locations>
  readonly spokenLanguages$: Observable<ProSpokenLanguage[]>

  readonly faPlus = faPlus

  readonly backButtonPath: string
  readonly optOutOfLayout = true

  isStickyFooterHidden = true

  private readonly proProfilesToIndex = ['otovo-schweiz-gmbh', 'tees-gmbh', 'zumbuehl-ag-umshaus-ch']

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event) {
    this.isStickyFooterHidden = !(event.target?.defaultView?.scrollY > 400)
  }

  constructor(
    route: ActivatedRoute,
    private readonly translationService: TranslocoService,
    private readonly linkService: LinkService,
    readonly configService: ConfigService,
    readonly breakpointService: BreakpointService,
    readonly supportService: SupportService,
  ) {
    super()
    this.backButtonPath = linkService.localizedPathToPreviousPage() ?? linkService.homePath
    this.pro$ = route.data.pipe(map(data => data['pro']))
    this.employeeCard$ = this.pro$.pipe(
      map(p =>
        p.publishedProfile
          ? {
              numEmployees: p.publishedProfile.numEmployees,
              numApprentices: p.publishedProfile.numApprentices,
              titleKey:
                p.publishedProfile.numApprentices > 0 ? 'profile.employeesWithApprentices' : 'profile.employees',
            }
          : undefined,
      ),
    )
    this.offeringSection$ = zip(
      this.pro$,
      translationService.selectTranslate('profile.featuredOfferings.plot'),
      translationService.selectTranslate('profile.featuredOfferings.title'),
    ).pipe(
      map(([pro, plot, title]) => {
        const profile = pro.publishedProfile ?? pro.minimalProfile
        if (profile?.featuredOfferings.length === 0) {
          return undefined
        } else {
          return {
            __type: 'OfferingsSection',
            id: pro.slug,
            title,
            plot,
            items: profile!.featuredOfferings.map(o => ({
              title: o.label,
              icon: o.icon ?? {collection: 'fal', name: 'ellipsis-h'},
              targetPath: pathToRequestOffering(linkService, o.id),
              targetType: 'job-request',
            })),
          }
        }
      }),
    )
    this.gallery$ = this.pro$.pipe(
      map(pro => pro.publishedProfile?.galleryImages ?? []),
      map(images =>
        images.map(i => ({
          title: i.title,
          description: i.description,
          url: i.image.url,
        })),
      ),
    )
    this.locations$ = this.pro$.pipe(
      map(pro => ({
        associations: pro.associations.map(a => this.toAssociationName(a)).filter((a): a is string => !!a),
        list: pro.locations.map(l => ({
          default: l.default,
          address: l.place,
          representativePerson: l.representativePerson,
          googlePlace: l.googlePlace,
        })),
      })),
    )
    this.spokenLanguages$ = this.pro$.pipe(
      map(pro => {
        return pro.spokenLanguages.map(lang => {
          switch (lang) {
            case ProSpokenLanguageGql.English:
              return ProSpokenLanguage.ENGLISH
            case ProSpokenLanguageGql.French:
              return ProSpokenLanguage.FRENCH
            case ProSpokenLanguageGql.Italian:
              return ProSpokenLanguage.ITALIAN
            case ProSpokenLanguageGql.German:
              return ProSpokenLanguage.GERMAN
          }
        })
      }),
    )
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.pro$.pipe(
      map(pro => ({
        doNotIndexPage: !this.proProfilesToIndex.includes(pro.slug),
        twitterCard: 'summary_with_large_image',
        title: `${pro.companyName} | Buildigo`,
        description: pro.publishedProfile?.aboutCompanyDescription ?? undefined,
        imageUrl: pro.publishedProfile?.logoImage?.url ?? undefined,
      })),
    )
  }

  startOtherRequest(pro: ProWithProfile) {
    return this.linkService.navigateTo(PUBLIC_ROUTES.JOB_REQUEST, {
      routeParams: {offeringId: OTHER_OFFERING_ID},
      queryParams: {pro: pro.slug},
    })
  }

  toAssociationName(association: ProAssociation): string | undefined {
    switch (association) {
      case ProAssociation.Bodenschweiz:
        return this.translationService.translate('profile.associations.bodenschweiz')
      case ProAssociation.Kuecheschweiz:
        return this.translationService.translate('profile.associations.kuecheschweiz')
      case ProAssociation.Eit:
        return this.translationService.translate('profile.associations.eit')
      case ProAssociation.Jardin:
        return this.translationService.translate('profile.associations.jardin')
      case ProAssociation.Suissetec:
        return this.translationService.translate('profile.associations.suissetec')
      case ProAssociation.Smvg:
        return this.translationService.translate('profile.associations.smvg')
      case ProAssociation.Vssm:
        return this.translationService.translate('profile.associations.vssm')
      default:
        return undefined
    }
  }

  profileIntro(pro: ProWithProfile): ProfileIntro {
    // sorting locations by default
    const locations = pro.locations.sort(defaultLocationFirst)
    return {
      companyName: pro.companyName,
      industries: pro.industries,
      recommendingAgency: pro.recommendingAgency?.name,
      locations,
      profile: {
        yearsAsTopPartner: pro.publishedProfile?.yearsAsTopPartner || pro.minimalProfile?.yearsAsTopPartner,
        ...(pro.publishedProfile?.logoImage?.url || pro.minimalProfile?.logoImage?.url
          ? {logoImage: pro.publishedProfile?.logoImage || pro.minimalProfile?.logoImage}
          : {}),
      },
      assignmentRegions: pro.assignmentRegions,
    }
  }

  breadcrumbs(): Breadcrumb[] {
    return [
      {
        name: this.translationService.translate('profile.breadcrumbs.craftsmen'),
        link: this.linkService.localizedPathTo(PUBLIC_ROUTES.PRO_DIRECTORY_PAGE),
      },
      {name: this.translationService.translate('profile.breadcrumbs.companyProfile')},
    ]
  }

  isRecommendedByShown(name?: string): boolean {
    if (!name) return false
    return !name.toLowerCase().includes('buildigo')
  }
}

function defaultLocationFirst(a: {default: boolean}, b: {default: boolean}) {
  if (a.default) {
    return -1
  }
  if (b.default) {
    return 1
  }
  return 0
}
